import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Rating,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import LocationOn from "../../icon/Loaction.svg";
import GroupIcon from "../../icon/Group.svg";
import NavigationBar from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import axios from "axios";
import img from "../../images/agencyportfolioimg.svg";
import { AuthContext } from "../../context/authcontext";
import { API_CONFIG } from "../../api/apiUrl";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface Contractor {
  id: number;
  name: string;
  description: string;
  address: string;
  pincode: number;
  projects: number;
  experience: number;
  rating: number;
  companyLogo: string;
  imageUrl: string;
  speciality: string;
  cities: { id: number; name: string | null };
  createdBy?: {
    id: number;
    login: string;
    firstName: string;
  };
}

interface PortfolioItem {
  message: string;
  image: string;
}

interface RateCardItem {
  id: number;
  name: string;
  description: string;
  remarks: string;
  isDeleted: boolean;
  isActive: boolean;
  rateCardTypes: {
    id: number;
    name: string | null;
    isActive: boolean | null;
    isDeleted: boolean | null;
    defaultPrice: number | null;
    remarks: string | null;
  };
}

interface PricingPlan {
  rateCardTypeId: number;
  name: string | null;
  price: number | null;
  rateCardItems: RateCardItem[];
  editing: boolean;
  features: { id: number; name: string; isEnabled: boolean }[];
}

const ContractorsOwnProfile = () => {
  const [selectedTab, setSelectedTab] = useState<string>("Pricing Plans");
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedPlanIndex, setExpandedPlanIndex] = useState<number | null>(null);

  const authContext = useContext(AuthContext);
  const isProvider = authContext?.authorities.includes("ROLE_PROVIDER") || false;
  const userProfileId = authContext?.userProfile?.id || null;
  const userId = authContext?.user?.id || null;
  const userLogin = authContext?.user?.login || "unknown";
  const userFirstName = authContext?.user?.firstName || "User";

  const tabs: string[] = ["Services We Provide", "Pricing Plans"];
  const token = localStorage.getItem("id_token");

  const navigate = useNavigate();

  const fetchContractor = async () => {
    if (!userProfileId || !isProvider) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get<Contractor[]>(API_CONFIG.COMPANY_PROFILE(userProfileId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.length > 0) {
        setContractor(response.data[0]);
      } else {
        setContractor(null);
      }
    } catch (error) {
      setContractor(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchRateCardData = async () => {
    try {
      const response = await axios.get(API_CONFIG.COMPANY_RATES, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const plans: PricingPlan[] = [];
      const processedTypeIds = new Set();

      response.data.forEach((item: any) => {
        if (item.rateCardTypes && item.companyRates) {
          const typeId = item.rateCardTypes.id;
          processedTypeIds.add(typeId);

          const features = item.rateCardItems?.map((feature: any) => ({
            id: feature.rateCardItems.id,
            name: feature.rateCardItems.name,
            isEnabled: true
          })) || [];

          plans.push({
            rateCardTypeId: typeId,
            name: item.rateCardTypes.name,
            price: item.companyRates.price,
            rateCardItems: item.rateCardItems?.map((i: any) => i.rateCardItems) || [],
            editing: false,
            features: features
          });
        }
      });

      response.data.forEach((item: any) => {
        if (item.rateCardNotMapped && item.rateCardNotMapped.length > 0) {
          item.rateCardNotMapped.forEach((unmappedItem: any) => {
            const typeId = unmappedItem.rateCardTypes.id;

            if (processedTypeIds.has(typeId)) return;

            processedTypeIds.add(typeId);

            const features = unmappedItem.rateCardItems.map((feature: any) => ({
              id: feature.id,
              name: feature.name,
              isEnabled: true
            }));

            plans.push({
              rateCardTypeId: typeId,
              name: unmappedItem.rateCardTypes.name,
              price: unmappedItem.rateCardTypes.defaultPrice,
              rateCardItems: unmappedItem.rateCardItems,
              editing: false,
              features: features
            });
          });
        }
      });

      setPricingPlans(plans);
    } catch (error) {
      console.error("Error", error);
      setPricingPlans([]);
      toast.error("Failed to fetch rate card data");
    }
  };

  useEffect(() => {
    fetchContractor();
    fetchRateCardData();
  }, [userProfileId]);

  const handleTabClick = (tab: string) => setSelectedTab(tab);

  const handleEditPrice = (index: number) => {
    const updatedPlans = [...pricingPlans];
    updatedPlans[index].editing = true;
    setPricingPlans(updatedPlans);
  };

  const handleSavePrice = async (index: number) => {
    if (!contractor || !userId) {
      toast.error("Missing something");
      return;
    }

    const plan = pricingPlans[index];
    try {
      const payload = {
        rateCardTypes: {
          id: plan.rateCardTypeId,
        },
        companyRates: {
          price: plan.price,
          companyProfile: {
            id: contractor.id,
          },
          user: {
            id: userId,
            login: userLogin,
            firstName: userFirstName,
          },
          rateCardTypes: {
            id: plan.rateCardTypeId,
          },
        },
        rateCardItems: plan.features
          .filter((feature) => feature.isEnabled)
          .map((feature) => ({
            rateCardItems: {
              id: feature.id,
            },
          })),
      };

      await axios.post(API_CONFIG.COMPANY_RATES, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const updatedPlans = [...pricingPlans];
      updatedPlans[index].editing = false;
      setPricingPlans(updatedPlans);
      toast.success(`Price for ${plan.name} updated successfully!`);

      fetchRateCardData();
    } catch (error) {
      toast.error("Failed to update price. Please try again.");
    }
  };

  const handlePriceChange = (index: number, newPrice: string) => {
    const updatedPlans = [...pricingPlans];
    updatedPlans[index].price = Number(newPrice) || 0;
    setPricingPlans(updatedPlans);
  };

  const handleFeatureChange = (planIndex: number, featureIndex: number, checked: boolean) => {
    const updatedPlans = [...pricingPlans];
    const plan = updatedPlans[planIndex];
    if (plan.features && featureIndex >= 0 && featureIndex < plan.features.length) {
      plan.features[featureIndex].isEnabled = checked;
      setPricingPlans(updatedPlans);
    }
  };

  const handleToggleFeatures = (planIndex: number) => {
    setExpandedPlanIndex(expandedPlanIndex === planIndex ? null : planIndex);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
        <p>Loading, please wait...</p>
      </div>
    );
  }

  if (!isProvider) {
    return <Typography align="center" sx={{ p: 2 }}>You do not have permission to view this profile.</Typography>;
  }

  if (!contractor) {
    return (
      <>
        <NavigationBar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, width: '100%', my: 8 }}>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              background: '#fafafa',
              p: 4,
              textAlign: 'center',
              maxWidth: 500,
              width: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: '#B69D74', fontWeight: 600, mb: 1 }}>
              No Profile Created
            </Typography>
            <Typography sx={{ color: '#666', fontSize: '16px' }}>
              You have not created your contractor profile yet. Please create your profile to get started and unlock all features.
            </Typography>
          </Box>
        </Box>
        <Footer />
      </>
    );
  }

  return (
    <>
      <NavigationBar />
      <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}>
        <Box sx={{ py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 }, px: { xs: 2, sm: 5, md: 4, lg: 4, xl: 6 } }}>
          <Grid container spacing={0}>
            <Grid item xs={12} md={12} lg={4}>
              <Box>
                <Typography sx={styles.agencyName}>{contractor.name}</Typography>
                <Typography sx={styles.agencyDescription}>{contractor.description}</Typography>
                <Typography sx={styles.specialtyText}>{contractor.speciality}</Typography>
                <Box sx={styles.section}>
                  <img src={LocationOn} alt="" />
                  <Typography sx={styles.locationText}>
                    {contractor.address}, {contractor.cities?.name}
                  </Typography>
                </Box>
                <Box sx={styles.section}>
                  <img src={GroupIcon} alt="" />
                  <Typography sx={styles.locationText}>{contractor.projects} Projects</Typography>
                </Box>
                <Box sx={styles.section}>
                  <img src={GroupIcon} alt="" />
                  <Typography sx={styles.locationText}>{contractor.experience} Years of Experience</Typography>
                </Box>
                <Box sx={styles.section}>
                  <Rating
                    value={contractor.rating}
                    readOnly
                    precision={0.1}
                    sx={{
                      color: "black",
                      "& .MuiRating-icon": {
                        fontSize: { xs: "16px", sm: "20px", md: "20px" },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={12} lg={8}>
              <Box sx={{ display: "flex", gap: { xs: "5px", sm: "16px", md: "30px" }, marginBottom: "20px" }}>
                {tabs.map((tab) => (
                  <Button key={tab} onClick={() => handleTabClick(tab)} sx={styles.tabButton(selectedTab, tab)}>
                    {tab}
                  </Button>
                ))}
              </Box>
              <Box>
                {selectedTab === "Services We Provide" && (
                  <Grid container spacing={2} maxWidth={600}>
                    {services.map((service, index) => (
                      <Grid item xs={6} sm={4} md={3} lg={4} key={index}>
                        <Card
                          elevation={0}
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "34px",
                            border: "1px solid #161717",
                            borderRadius: "25px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "15px", sm: "16px", md: "16px", lg: "17px", xl: "19px" },
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              padding: "0 10px",
                            }}
                          >
                            {service}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {selectedTab === "Pricing Plans" && (
                  <Grid container spacing={2}>
                    {pricingPlans.map((plan, planIndex) => (
                      <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={planIndex}>
                        <Card elevation={0} sx={styles.pricingCard}>
                          <CardContent>
                            <Typography sx={styles.pricingTitle}>{plan.name || "Unnamed Plan"}</Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "10px" }}>
                              {plan.editing ? (
                                <>
                                  <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
                                    <TextField
                                      value={plan.price || ""}
                                      onChange={(e) => handlePriceChange(planIndex, e.target.value)}
                                      variant="outlined"
                                      size="small"
                                      type="number"
                                      sx={{
                                        width: "80px",
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "12px",
                                          bgcolor: "#f5f5f5",
                                          "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#89652A",
                                          },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#89652A",
                                            borderWidth: "2px",
                                          },
                                        },
                                      }}
                                    />
                                    <Button
                                      variant="contained"
                                      onClick={() => handleSavePrice(planIndex)}
                                      sx={{
                                        backgroundColor: "#89652A",
                                        color: "white",
                                        borderRadius: "12px",
                                        textTransform: "none",
                                      }}
                                    >
                                      Save
                                    </Button>
                                  </Box>
                                  <Box sx={{ width: "100%", mt: 1 }}>
                                    {plan.features.map((feature, featureIndex) => (
                                      <FormControlLabel
                                        key={featureIndex}
                                        control={
                                          <Checkbox
                                            checked={feature.isEnabled}
                                            onChange={(e) => handleFeatureChange(planIndex, featureIndex, e.target.checked)}
                                            sx={{
                                              color: "#89652A",
                                              "&.Mui-checked": { color: "#89652A" },
                                            }}
                                          />
                                        }
                                        label={feature.name}
                                        sx={{
                                          display: "flex",
                                          "& .MuiFormControlLabel-label": {
                                            fontSize: "14px",
                                            fontWeight: 400,
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </>
                               //  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                //   <Typography sx={styles.pricingPrice}>₹{plan.price || 0}</Typography>
                                //   {isProvider && (
                                //     <IconButton
                                //       size="small"
                                //       onClick={() => handleEditPrice(planIndex)}
                                //       sx={{
                                //         color: "#89652A",
                                //         "&:hover": {
                                //           backgroundColor: "rgba(137, 101, 42, 0.1)",
                                //         },
                                //       }}
                                //     >
                                //       <EditIcon fontSize="small" />
                                //     </IconButton>
                                //   )}
                                // </Box>
                              ) : (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography sx={styles.pricingPrice}>₹{plan.price || 0}</Typography>
                                </Box>
                              )}
                            </Box>
                            <Typography sx={styles.pricingSubtitle}>Per Square Feet</Typography>
                            {!plan.editing && (
                              <Box>
                                {plan.features
                                  .slice(0, expandedPlanIndex === planIndex ? plan.features.length : 4)
                                  .map((feature, featureIndex) => (
                                    <Typography key={featureIndex} sx={styles.featureText}>
                                      <span style={styles.featureIcon}>
                                        <CheckCircleIcon />
                                      </span>
                                      {feature.name}
                                    </Typography>
                                  ))}
                                {plan.features.length > 4 && (
                                  <Box sx={{display:"flex",justifyContent:"center"}}>
<Button
                                    onClick={() => handleToggleFeatures(planIndex)}
                                    sx={{
                                  
                                      textTransform: "none",
                                      color: "#89652A",
                                      fontSize: "13px",
                                      mt: 1,
                                    }}
                                  >
                                    {expandedPlanIndex === planIndex ? "View Less" : "View More"}
                                  </Button>
                                  </Box>

                                )}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
          <Box>
            <Typography
              sx={{
                my: { xs: 2, md: 4 },
                fontSize: { xs: "30px", md: "50px" },
                textTransform: "capitalize",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Portfolio
            </Typography>
            <Box sx={{ border: "1px solid #E2DFDF", padding: { xs: "15px", sm: "16px", md: "50px" }, borderRadius: "10px" }}>
              <Grid container spacing={{ xs: 2, md: 2 }}>
                {Portfolio.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ border: "1px solid #E2DFDF", borderRadius: "10px", padding: "20px" }}>
                      <img src={item.image} alt="Portfolio Item" style={{ width: "100%", borderRadius: "7px" }} />
                      <Typography
                        sx={{
                          fontSize: { xs: "15px", sm: "15px", md: "15px", lg: "15px", xl: "15px" },
                          fontWeight: 400,
                          my: 2,
                          textAlign: "center",
                        }}
                      >
                        {item.message}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
        <Footer />
      </Box>
    </>
  );
};

export default ContractorsOwnProfile;

const styles = {
  agencyName: {
    textTransform: "capitalize",
    fontSize: { xs: "31px", sm: "35px", md: "35px", lg: "35px", xl: "35px" },
    fontWeight: 600,
    mb: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  agencyDescription: {
    textTransform: "capitalize",
    fontSize: { xs: "15px", sm: "18px", md: "18px", lg: "17px", xl: "19px" },
    fontWeight: 400,
  },
  specialtyText: {
    textTransform: "capitalize",
    mt: 2,
    fontWeight: 400,
    fontSize: { xs: "14px", sm: "14px", md: "14px", lg: "16px", xl: "15px" },
    mb: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  locationText: {
    textTransform: "capitalize",
    fontWeight: 400,
    fontSize: { xs: "14px", sm: "14px", md: "14px", lg: "15px", xl: "15px" },
    my: 0.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  section: { display: "flex", alignItems: "center", gap: 1, mb: 0.7 },
  tabButton: (selectedTab: string, tab: string) => ({
    textTransform: "none",
    fontWeight: 500,
    fontSize: { xs: "15px", sm: "17px", md: "17px", lg: "17px", xl: "18px" },
    backgroundColor: selectedTab === tab ? "#89652A" : "transparent",
    color: selectedTab === tab ? "#fff" : "black",
    border: selectedTab === tab ? "1px solid #89652A" : "1px solid black",
    borderRadius: "50px",
    padding: { xs: "4px 16px", sm: "5px 24px", md: "5px 30px" },
  }),
  pricingCard: {
    textTransform: "capitalize",
    flex: 1,
    padding: { xs: "3px", sm: "2px", md: "1px", lg: "1px", xl: "3px" },
    borderRadius: "10px",
    border: "0.2px solid #161717",
  },
  pricingTitle: {
    textTransform: "capitalize",
    fontSize: "20px",
    fontWeight: 600,
    margin: "0 auto",
    border: "0.2px solid #161717",
    maxWidth: "fit-content",
    padding: "1px 25px",
    borderRadius: "20px",
  },
  pricingPrice: { fontSize: "40px", fontWeight: 600, textAlign: "center" },
  pricingSubtitle: {
    textTransform: "capitalize",
    marginBottom: "5px",
    fontSize: "20px",
    fontWeight: 500,
    textAlign: "center",
  },
  featureText: {
    textTransform: "capitalize",
    fontSize: { xs: "15px", sm: "15px", md: "15px", lg: "15px", xl: "15px" },
    fontWeight: 400,
    textAlign: "start",
    display: "flex",
    gap: { xs: "9px", sm: "7px", md: "7px", lg: "4px", xl: "9px" },
  },
  featureIcon: { color: "#000" },
};

const Portfolio: PortfolioItem[] = [
  { message: "Sign Up & View Verified List of Contractors As Per Location", image: img },
  { message: "Sign Up & View Verified List of Contractors As Per Location", image: img },
  { message: "Sign Up & View Verified List of Contractors As Per Location", image: img },
];

const services: string[] = [
  "Civil Construction",
  "Swimming",
  "Elevation",
  "Flooring/ Tiling",
  "Garden",
  // "Grills and Fabrication",
  "Ceiling Work",
  "Water Tank",
  "Waterproofing",
];